import React from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {
	FlexColumn,
	FlexRow,
	Icon,
	NestedCard,
	NumberField,
	SubHeading,
	Separator,
	Text,
	TextField,
} from '@forecast-it/design-system';
import {MODAL_TYPE, showModal} from '../../../../shared/components/modals/generic_modal_conductor';
import {formatHoursValue} from '../baselineTemplateFormat';
import {EstimateRowsPatch, EstimateSections} from './EstimateSections';
import {TemplateDeliverableCard} from './TemplateDeliverableCard';
import {TemplateSelectDeliverableCard} from './TemplateSelectDeliverableCard';
import {showTemplateDeliverableModal} from './TemplateDeliverableModal';
import {showChangeTemplateDeliverableModal} from './ChangeTemplateDeliverableModal';
import {
	addDeliverableToPhase,
	changeDeliverable,
	DeliverableDraft,
	editDeliverable,
	linkDeliverable,
	Option,
	PhaseDraft,
	phaseHours,
	removeDeliverableFromPhase,
	replaceRowsFor,
	rowsFor,
	TemplateDraft,
	toNullableNumber,
} from './baselineTemplateDraft';

// NestedCard's content padding is tight for a form, so the body adds its own.
const Body = Styled(FlexColumn)`
	padding: 8px;
`;

type Props = {
	phase: PhaseDraft;
	index: number;
	includeDeliverables: boolean;
	canDelete: boolean;
	roles: Option[];
	categories: Option[];
	heldRoles?: Readonly<Record<string, Option>>;
	heldCategories?: Readonly<Record<string, Option>>;
	currency: string;
	fieldError: (path: string) => string | undefined;
	onChange: (patch: Partial<PhaseDraft>, changedPath?: string) => void;
	onDelete: () => void;
	/** The template's deliverables; the phase shows the ones it links. */
	deliverables: DeliverableDraft[];
	/** Applies a change that reaches beyond this phase, such as adding a template deliverable. */
	onTemplateChange: (update: (draft: TemplateDraft) => TemplateDraft) => void;
};

/** The template deliverables a phase links, in card order. A key with no deliverable behind it is left out. */
const linkedDeliverablesOf = (phase: PhaseDraft, deliverables: DeliverableDraft[]): DeliverableDraft[] => {
	const byKey = new Map(deliverables.map(deliverable => [deliverable.key, deliverable]));
	return phase.deliverableKeys.flatMap(key => {
		const deliverable = byKey.get(key);
		return deliverable ? [deliverable] : [];
	});
};

type PhaseDeliverablesProps = Pick<
	Props,
	| 'phase'
	| 'index'
	| 'deliverables'
	| 'roles'
	| 'categories'
	| 'heldRoles'
	| 'heldCategories'
	| 'currency'
	| 'fieldError'
	| 'onChange'
	| 'onTemplateChange'
>;

/** A phase's deliverable cards, its No deliverable card when it has phase-level lines, and the Select card. */
const PhaseDeliverables = ({
	phase,
	index,
	deliverables,
	roles,
	categories,
	heldRoles,
	heldCategories,
	currency,
	fieldError,
	onChange,
	onTemplateChange,
}: PhaseDeliverablesProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const linked = linkedDeliverablesOf(phase, deliverables);
	const unlinked = deliverables.filter(deliverable => !phase.deliverableKeys.includes(deliverable.key));
	const phaseLevelLineCount = rowsFor(phase.timeEstimates, null).length + rowsFor(phase.expenseEstimates, null).length;

	// A card edits only its own rows; they go back among the phase's rows in place.
	const rowsChange = (deliverableKey: string | null) => (patch: EstimateRowsPatch, changedPath?: string) =>
		onChange(
			{
				...(patch.timeEstimates
					? {timeEstimates: replaceRowsFor(phase.timeEstimates, deliverableKey, patch.timeEstimates)}
					: {}),
				...(patch.expenseEstimates
					? {expenseEstimates: replaceRowsFor(phase.expenseEstimates, deliverableKey, patch.expenseEstimates)}
					: {}),
			},
			changedPath
		);

	const openChange = (current: DeliverableDraft | null) =>
		showChangeTemplateDeliverableModal({
			options: deliverables.filter(deliverable => deliverable.key !== current?.key),
			current,
			onChange: to => onTemplateChange(draft => changeDeliverable(draft, phase.key, current?.key ?? null, to)),
		});

	const cardProps = (deliverable: DeliverableDraft | null) => {
		const deliverableKey = deliverable?.key ?? null;
		return {
			deliverable,
			timeEstimates: rowsFor(phase.timeEstimates, deliverableKey),
			expenseEstimates: rowsFor(phase.expenseEstimates, deliverableKey),
			roles,
			categories,
			heldRoles,
			heldCategories,
			currency,
			fieldError,
			onChangeRows: rowsChange(deliverableKey),
			onChangeDeliverable: () => openChange(deliverable),
			canChange: deliverables.some(other => other.key !== deliverableKey),
		};
	};

	return (
		<FlexColumn gap="m">
			{linked.map((deliverable, cardIndex) => (
				<TemplateDeliverableCard
					key={deliverable.key}
					cyPrefix={`baseline-template-phase-${index}-deliverable-${cardIndex}`}
					{...cardProps(deliverable)}
					onEditDeliverable={() =>
						showTemplateDeliverableModal({
							deliverable: {name: deliverable.name, description: deliverable.description},
							onSave: fields => onTemplateChange(draft => editDeliverable(draft, deliverable.key, fields)),
						})
					}
					onRemoveFromPhase={() =>
						showModal({
							type: MODAL_TYPE.REMOVE_DELIVERABLE_FROM_PHASE_MODAL,
							callback: () =>
								onTemplateChange(draft => removeDeliverableFromPhase(draft, phase.key, deliverable.key)),
						})
					}
				/>
			))}
			{phaseLevelLineCount > 0 ? (
				<TemplateDeliverableCard cyPrefix={`baseline-template-phase-${index}-no-deliverable`} {...cardProps(null)} />
			) : null}
			<TemplateSelectDeliverableCard
				cyPrefix={`baseline-template-phase-${index}-select-deliverable`}
				label={formatMessage({
					id: linked.length > 0 ? 'deliverable.select_another_deliverable' : 'deliverable.select_deliverable',
				})}
				available={unlinked}
				onSelect={deliverableKey => onTemplateChange(draft => linkDeliverable(draft, phase.key, deliverableKey))}
				onAddNew={() =>
					showTemplateDeliverableModal({
						onSave: fields => onTemplateChange(draft => addDeliverableToPhase(draft, phase.key, fields)),
					})
				}
			/>
		</FlexColumn>
	);
};

export const PhaseEditor = ({
	phase,
	index,
	includeDeliverables,
	canDelete,
	roles,
	categories,
	heldRoles,
	heldCategories,
	currency,
	fieldError,
	onChange,
	onDelete,
	deliverables,
	onTemplateChange,
}: Props): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const cy = (part: string) => `baseline-template-phase-${index}-${part}`;
	const linkedCount = linkedDeliverablesOf(phase, deliverables).length;

	return (
		<NestedCard colorTheme="dark" dataCy={cy('card')}>
			{/* The menu is always shown. Delete phase is disabled while this is the only phase. */}
			<NestedCard.Header
				mainIcon="phase"
				sectionName={formatMessage({id: 'baseline_templates.phase.number'}, {number: index + 1})}
				name={phase.name.trim() || undefined}
				showContextMenu
				contextMenuOptions={[
					{
						name: formatMessage({id: 'baseline_templates.phase.delete'}),
						onClick: onDelete,
						disabled: !canDelete,
					},
				]}
			>
				{includeDeliverables && linkedCount > 0 ? (
					// FlexRow does not pass accessibility attributes on, so the labelled wrapper is a plain element.
					<div
						role="img"
						aria-label={`${formatMessage({id: 'common.deliverables'})}: ${linkedCount}`}
						data-cy={cy('deliverable-count')}
					>
						<FlexRow gap="xs" alignItems="center">
							<Icon icon="deliverable" size="m" aria-hidden="true" />
							<SubHeading>{linkedCount}</SubHeading>
						</FlexRow>
					</div>
				) : null}
				{phase.startWeek ? (
					<Text>{formatMessage({id: 'baseline_templates.phase.starts_week'}, {week: phase.startWeek})}</Text>
				) : null}
				{phase.durationDays ? (
					<Text>{formatMessage({id: 'baseline_templates.phase.days'}, {days: phase.durationDays})}</Text>
				) : null}
				<SubHeading>{formatHoursValue(intl, phaseHours(phase))}</SubHeading>
			</NestedCard.Header>
			<NestedCard.UncollapsibleContentWrapper>
				<NestedCard.Content>
					<Body gap="l">
						<FlexRow gap="m" alignItems="flex-start">
							<div data-field-path={`${phase.key}.name`} style={{flex: 2}}>
								<TextField
									label={formatMessage({id: 'baseline_templates.phase.name'})}
									data-cy={cy('name')}
									placeholder={formatMessage({id: 'baseline_templates.phase.name_placeholder'})}
									value={phase.name}
									onValueChange={(value: string) => onChange({name: value}, `${phase.key}.name`)}
									hasError={!!fieldError(`${phase.key}.name`)}
									errorText={fieldError(`${phase.key}.name`)}
								/>
							</div>
							<div data-field-path={`${phase.key}.startWeek`} style={{flex: 1}}>
								<NumberField
									label={formatMessage({id: 'baseline_templates.phase.start_week'})}
									data-cy={cy('start-week')}
									value={phase.startWeek ?? Number.NaN}
									onChange={(value: number) =>
										onChange({startWeek: toNullableNumber(value)}, `${phase.key}.startWeek`)
									}
									allowEmpty
									minValue={1}
									maximumFractionDigits={0}
									hasError={!!fieldError(`${phase.key}.startWeek`)}
									errorText={fieldError(`${phase.key}.startWeek`)}
								/>
							</div>
							<div data-field-path={`${phase.key}.durationDays`} style={{flex: 1}}>
								<NumberField
									label={formatMessage({id: 'baseline_templates.phase.duration_days'})}
									data-cy={cy('duration')}
									value={phase.durationDays ?? Number.NaN}
									onChange={(value: number) =>
										onChange({durationDays: toNullableNumber(value)}, `${phase.key}.durationDays`)
									}
									allowEmpty
									minValue={1}
									maximumFractionDigits={0}
									hasError={!!fieldError(`${phase.key}.durationDays`)}
									errorText={fieldError(`${phase.key}.durationDays`)}
								/>
							</div>
						</FlexRow>

						<Separator />
						{includeDeliverables ? (
							<PhaseDeliverables
								phase={phase}
								index={index}
								deliverables={deliverables}
								roles={roles}
								categories={categories}
								heldRoles={heldRoles}
								heldCategories={heldCategories}
								currency={currency}
								fieldError={fieldError}
								onChange={onChange}
								onTemplateChange={onTemplateChange}
							/>
						) : (
							<EstimateSections
								cyPrefix={`baseline-template-phase-${index}`}
								deliverableKey={null}
								timeEstimates={phase.timeEstimates}
								expenseEstimates={phase.expenseEstimates}
								roles={roles}
								categories={categories}
								heldRoles={heldRoles}
								heldCategories={heldCategories}
								currency={currency}
								fieldError={fieldError}
								onChange={onChange}
							/>
						)}
					</Body>
				</NestedCard.Content>
			</NestedCard.UncollapsibleContentWrapper>
		</NestedCard>
	);
};
